import { __awaiter, __rest } from "tslib";
import { GRID_OPTIONS_BY_CELL_DIMENSION, GRID_OPTIONS_BY_CELL_GAP, } from "./gridConfigConstants";
import { bayLevelHasCenterLineRow, getRowsAndTiersFromSlotKeys, } from "@tedivo/tedivo-bay-grid-pure";
import { createGridFromConfig, } from "@tedivo/tedivo-bay-grid-core";
import { IntersectStatusEnum, createObserverWithCallback, removeChildren, } from "@tedivo/tedivo-dom-helpers";
import SizeSmallMidLargeEnum from "../../../../../app/enums/SizeSmallMidLargeEnum";
import { getHeaderDetailPills } from "./getHeaderDetailPills";
import globalUnits from "../../../../../app/units/globalUnits";
import { pad2 } from "@tedivo/tedivo-pure-helpers";
const EXTRA_SPACE_TOP = 60;
class BayBoxComponent extends HTMLElement {
    constructor() {
        super();
        this.data = undefined;
        this.nodesOptions = undefined;
        this._scale = 1;
        this.createGridOfBay = (bayBox) => {
            return () => {
                var _a, _b;
                if (!this.data)
                    return;
                removeChildren(bayBox);
                const { sizeSummary, shipData, lidData, bays, isoBay, bayAbove, bayBelow, enabledCells, slotsDataAbove, slotsDataBelow, slotsData, width, height, ssMaxRow, cellSize, cellSeparation, validationResult, showBottomBases, showTCGs, } = this.data;
                this._holderIntersectionStatus = IntersectStatusEnum.Rendered;
                console.time(`createGridFromConfig ${isoBay}`);
                const top = document.createElement("div");
                top.setAttribute("part", "top");
                const h3 = document.createElement("h3");
                h3.setAttribute("part", "title");
                h3.innerHTML = bays.map((b, idx) => (idx === 0 ? b : `(${b})`)).join(" ");
                top.appendChild(h3);
                const editBtn = (_a = this.createEditBayButtonFunction) === null || _a === void 0 ? void 0 : _a.call(this, isoBay, sizeSummary, shipData, bayAbove, bayBelow, enabledCells, slotsDataAbove, slotsDataBelow);
                if (editBtn)
                    top.appendChild(editBtn);
                const detailsHolder = getHeaderDetailPills(bayAbove, bayBelow, validationResult);
                if (!detailsHolder.dataset.hasWarnings) {
                    this.setAttribute("validation-result", "valid");
                }
                else {
                    this.removeAttribute("validation-result");
                }
                bayBox.appendChild(top);
                bayBox.appendChild(detailsHolder);
                // Bottom Base: resolved per-bay (row override -> bay-common override -> ship-wide
                // master), and only for tiers that are actually a row's bottom tier in THIS bay.
                const bottomBaseLabelsByTier = showBottomBases
                    ? buildBottomBaseLabelsByTier(bayAbove, bayBelow, shipData.masterCGs)
                    : undefined;
                // TCG is per-row and per-level (above/below can legitimately differ), resolved
                // separately so the top ROW numbers show above-deck TCGs and the bottom ROW
                // numbers show below-deck TCGs.
                const aboveTcgLabelsByRow = showTCGs
                    ? buildTcgLabelsByRow(bayAbove, shipData.masterCGs.aboveTcgs, ssMaxRow)
                    : undefined;
                const belowTcgLabelsByRow = showTCGs
                    ? buildTcgLabelsByRow(bayBelow, shipData.masterCGs.belowTcgs, ssMaxRow)
                    : undefined;
                // gridNumbersFontSize/cgNumbersFontSize live in `nodesOptions`, not at the top
                // level, so they're pulled out of the per-cell-size config here instead of
                // being spread alongside it (where the grid renderer would silently ignore them).
                const _c = GRID_OPTIONS_BY_CELL_DIMENSION[cellSize], { gridNumbersFontSize, cgNumbersFontSize } = _c, cellDimensionOptions = __rest(_c, ["gridNumbersFontSize", "cgNumbersFontSize"]);
                const { svgGroup, symbols, cellPositions: gridCellPositions, width: gridWidth, } = createGridFromConfig(Object.assign(Object.assign(Object.assign(Object.assign({}, cellDimensionOptions), GRID_OPTIONS_BY_CELL_GAP[cellSeparation]), this.data.sizeSummary), { maxRow: ssMaxRow, centerLineRow: sizeSummary.centerLineRow ? 1 : 0, 
                    // A level without the centerline row is drawn without the 00 column
                    aboveCenterLineRow: bayLevelHasCenterLineRow(sizeSummary, bayAbove) ? 1 : 0, belowCenterLineRow: bayLevelHasCenterLineRow(sizeSummary, bayBelow) ? 1 : 0, nodesOptions: Object.assign(Object.assign({}, this.nodesOptions), { gridNumbersFontSize, cgNumbersFontSize }), enabledCells, cellsDrawFunction: (_b = this.createFnSlotCell) === null || _b === void 0 ? void 0 : _b.call(this, cellSize), cellsToDraw: slotsData, lidData, symbolPrefix: "", showBottomBases,
                    showTCGs,
                    bottomBaseLabelsByTier,
                    aboveTcgLabelsByRow,
                    belowTcgLabelsByRow }));
                // All bay boxes share the vessel-wide width: center a narrower grid (no 00 row) in it
                const gridOffsetX = Math.max(0, (width - gridWidth) * 0.5);
                if (gridOffsetX)
                    svgGroup.setAttribute("transform", `translate(${gridOffsetX}, 0)`);
                const cellPositions = offsetCellPositionsX(gridCellPositions, gridOffsetX);
                const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
                svg.setAttribute("width", `${width}px`);
                svg.setAttribute("height", `${height}px`);
                svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
                svg.setAttribute("part", "svg-bay");
                svg.setAttribute("role", "figure");
                if (symbols)
                    Object.keys(symbols).forEach((n) => svg.appendChild(symbols[n]));
                svg.appendChild(svgGroup);
                console.timeEnd(`createGridFromConfig ${isoBay}`);
                bayBox.appendChild(svg);
                if (cellSize === SizeSmallMidLargeEnum.XLARGE && this.drawStackWeights)
                    bayBox.appendChild(this.drawStackWeights({
                        bayAbove,
                        bayBelow,
                        cellPositions,
                        cellSize,
                        cellPadding: cellSeparation,
                    }));
            };
        };
        this._holderIntersectionStatus = IntersectStatusEnum.Initialized;
        this.bayBox = document.createElement("div");
        this.bayBox.setAttribute("part", "box");
        this.attachShadow({ mode: "open" });
    }
    get holderIntersectionStatus() {
        return this._holderIntersectionStatus;
    }
    get scale() {
        return this._scale;
    }
    set scale(value) {
        var _a, _b;
        if (value === this._scale)
            return;
        this._scale = value;
        this.setSize(((_a = this.data) === null || _a === void 0 ? void 0 : _a.width) || 0, ((_b = this.data) === null || _b === void 0 ? void 0 : _b.height) || 0);
    }
    setSize(width, height) {
        const bayBox = this.bayBox;
        const fullHeight = height + EXTRA_SPACE_TOP;
        // The box is drawn at its natural (unscaled) size and then visually scaled
        // down via `transform`. Scaling must be anchored at the top-left corner
        // (not the CSS default center) so the box stays put instead of sliding
        // down and to the right as it shrinks. `transform` doesn't shrink the
        // space the box reserves in the flex layout, so negative margins pull
        // that reserved footprint back down to match the visual (scaled) size.
        bayBox.style.width = `${width}px`;
        bayBox.style.height = `${fullHeight}px`;
        if (this._scale === 1) {
            bayBox.style.transform = ``;
            bayBox.style.transformOrigin = ``;
            bayBox.style.marginRight = ``;
            bayBox.style.marginBottom = ``;
        }
        else {
            bayBox.style.transform = `scale(${this._scale})`;
            bayBox.style.transformOrigin = `top left`;
            bayBox.style.marginRight = `${-(1 - this._scale) * width}px`;
            bayBox.style.marginBottom = `${-(1 - this._scale) * fullHeight}px`;
        }
        return this;
    }
    setData(data) {
        return __awaiter(this, void 0, void 0, function* () {
            this.setAttribute("data-bay", data.isoBay);
            this.data = data;
            this.drawWithObserver(this.shadowRoot || null);
            return this;
        });
    }
    setDrawingColors(nodesOptions) {
        this.nodesOptions = nodesOptions;
        return this;
    }
    setCreateFnSlotCell(fn) {
        this.createFnSlotCell = fn;
        return this;
    }
    setDrawingProps({ createEditBayButtonFunction, createFnSlotCell, drawStackWeights, }) {
        this.createFnSlotCell = createFnSlotCell;
        this.createEditBayButtonFunction = createEditBayButtonFunction;
        this.drawStackWeights = drawStackWeights;
        return this;
    }
    /** Prepares the box to be drawn when visible */
    drawWithObserver(root) {
        var _a;
        if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.isoBay) || !root) {
            return;
        }
        const bayBox = this.bayBox;
        removeChildren(bayBox);
        if (this.holderIntersectionStatus ===
            IntersectStatusEnum.WaitingForIntersection) {
            return;
        }
        this._holderIntersectionStatus = IntersectStatusEnum.WaitingForIntersection;
        const ioObserver = createObserverWithCallback(this.createGridOfBay(bayBox), { delay: 1 });
        ioObserver.observe(bayBox);
        root.appendChild(bayBox);
    }
    updateData(newData) {
        this.data = Object.assign(Object.assign({}, this.data), newData);
        if (this.shadowRoot) {
            removeChildren(this.bayBox);
            this.drawWithObserver(this.shadowRoot);
        }
        return this;
    }
    connectedCallback() {
        const shadowRoot = this.shadowRoot;
        this.drawWithObserver(shadowRoot);
    }
}
export default BayBoxComponent;
customElements.define("bay-box-component", BayBoxComponent);
function offsetCellPositionsX(cellPositions, dx) {
    if (!dx)
        return cellPositions;
    return Object.keys(cellPositions).reduce((acc, pos) => {
        acc[pos] = { x: cellPositions[pos].x + dx, y: cellPositions[pos].y };
        return acc;
    }, {});
}
/** A bay level's slot keys that are genuinely usable: not restricted and with at least one size defined. */
function getEffectiveSlotKeys(bl) {
    const perSlotInfo = bl === null || bl === void 0 ? void 0 : bl.perSlotInfo;
    if (!perSlotInfo)
        return [];
    return Object.keys(perSlotInfo).filter((sk) => perSlotInfo[sk].restricted !== 1 && Object.keys(perSlotInfo[sk].sizes || {}).length > 0);
}
/** Sort key matching `sortRowsArray`: port (even rows) sort before center ("00") before starboard (odd rows). */
function rowPortToStbdKey(row) {
    return Number(row) % 2 === 1 ? Number(row) : -Number(row);
}
/**
 * Formats each row's own Bottom Base value (row override -> bay-common override ->
 * ship-wide master fallback) for display on the regular grid, keyed by tier. Only
 * emits a label for a tier when it's actually a row's bottom (lowest occupied) tier
 * in THIS bay -- tiers this bay doesn't use at all, or that no row starts from, are
 * left out, so e.g. a bay whose lowest tier is 12 never shows labels for 02-10.
 */
function buildBottomBaseLabelsByTier(bayAbove, bayBelow, masterCGs) {
    const labels = {};
    [bayAbove, bayBelow].forEach((bl) => {
        const effectiveSlotKeys = getEffectiveSlotKeys(bl);
        if (effectiveSlotKeys.length === 0)
            return;
        const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(effectiveSlotKeys);
        rows.forEach((row) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const bottomTier = pad2(tiersByRow[row].minTier);
            if (labels[bottomTier] !== undefined)
                return;
            const bottomBase = (_g = (_d = (_c = (_b = (_a = bl === null || bl === void 0 ? void 0 : bl.perRowInfo) === null || _a === void 0 ? void 0 : _a.each) === null || _b === void 0 ? void 0 : _b[row]) === null || _c === void 0 ? void 0 : _c.bottomBase) !== null && _d !== void 0 ? _d : (_f = (_e = bl === null || bl === void 0 ? void 0 : bl.perRowInfo) === null || _e === void 0 ? void 0 : _e.common) === null || _f === void 0 ? void 0 : _f.bottomBase) !== null && _g !== void 0 ? _g : masterCGs.bottomBases[bottomTier];
            if (bottomBase !== undefined)
                labels[bottomTier] = globalUnits.lengthUnits.fromValueToFormattedDisplay(globalUnits.vcgTransposer.fromValueToDisplay(bottomBase));
        });
    });
    return labels;
}
/**
 * Formats a bay level's TCG values (per row) for display on the regular grid: row
 * override, falling back to that level's master TCGs (`masterCGs.aboveTcgs` for the
 * above-deck level, `masterCGs.belowTcgs` for the below-deck level). Only rows from
 * the port-most used row through the starboard-most used row are included; rows in
 * between are still shown even if empty/restricted, but rows outside that used range
 * are left out entirely.
 */
function buildTcgLabelsByRow(bayLevel, masterTcgsForLevel, maxRow) {
    var _a, _b, _c, _d;
    const labels = {};
    const usedRows = getRowsAndTiersFromSlotKeys(getEffectiveSlotKeys(bayLevel)).rows;
    if (usedRows.length === 0)
        return labels;
    const minKey = rowPortToStbdKey(usedRows[0]);
    const maxKey = rowPortToStbdKey(usedRows[usedRows.length - 1]);
    for (let row = 0; row <= maxRow; row += 1) {
        const isoRow = pad2(row);
        const key = rowPortToStbdKey(isoRow);
        if (key < minKey || key > maxKey)
            continue;
        const tcg = (_d = (_c = (_b = (_a = bayLevel === null || bayLevel === void 0 ? void 0 : bayLevel.perRowInfo) === null || _a === void 0 ? void 0 : _a.each) === null || _b === void 0 ? void 0 : _b[isoRow]) === null || _c === void 0 ? void 0 : _c.tcg) !== null && _d !== void 0 ? _d : masterTcgsForLevel[isoRow];
        if (tcg !== undefined)
            labels[isoRow] = globalUnits.lengthUnits.fromValueToFormattedDisplay(globalUnits.tcgTransposer.fromValueToDisplay(tcg));
    }
    return labels;
}
